import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePresenceChannel } from '../hooks/usePagePresence';

/**
 * One /ws/presence/ socket per tab, shared by the header's page avatars and
 * every table's row markers.
 *
 * Split in two so the busy pages don't re-render on presence traffic:
 *   - PresenceActionsContext: { setFocus, clearFocus } -- stable for the
 *     socket's lifetime; this is all a page (via useGridPresence) needs.
 *   - PresenceStateContext: { others, peers, records } -- changes only when someone
 *     arrives, leaves or moves; read by the small marker components.
 */
// Exported so tests (and any future story) can supply peers without a socket.
export const PresenceStateContext = createContext({ others: [], peers: [], records: [] });
const PresenceActionsContext = createContext({ setFocus: () => {}, clearFocus: () => {} });

export const PresenceProvider = ({ children }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { others, peers, records, setFocus, clearFocus } = usePresenceChannel(location.pathname, {
    enabled: isAuthenticated,
    selfEmail: user?.email,
  });

  const actions = useMemo(() => ({ setFocus, clearFocus }), [setFocus, clearFocus]);
  const state = useMemo(() => ({ others, peers, records }), [others, peers, records]);

  return (
    <PresenceActionsContext.Provider value={actions}>
      <PresenceStateContext.Provider value={state}>{children}</PresenceStateContext.Provider>
    </PresenceActionsContext.Provider>
  );
};

export const usePresenceState = () => useContext(PresenceStateContext);
export const usePresenceActions = () => useContext(PresenceActionsContext);

/**
 * Other people whose focus is on `rowId` in `grid`: [{ member, editing }],
 * one per person (editing wins if any of their tabs is editing it).
 */
export const useRowPeers = (grid, rowId) => {
  const { peers } = usePresenceState();
  return useMemo(() => {
    const key = String(rowId);
    const byEmail = new Map();
    peers.forEach(({ member, focus }) => {
      if (!focus || focus.grid !== grid || !focus.rows.includes(key)) return;
      const editing = focus.editing === key;
      const seen = byEmail.get(member.email);
      byEmail.set(member.email, { member, editing: editing || Boolean(seen?.editing) });
    });
    return Array.from(byEmail.values());
  }, [peers, grid, rowId]);
};

/**
 * Other people editing `rowId` of `grid` right now: on this page (their focus
 * has `editing === rowId`) or -- for shared records such as time entries --
 * on any page of the teamspace (Time Entries <-> Calendar). One per person.
 */
export const useEditingPeers = (grid, rowId) => {
  const { peers, records = [] } = usePresenceState();
  return useMemo(() => {
    if (rowId == null) return [];
    const key = String(rowId);
    const byEmail = new Map();
    peers.forEach(({ member, focus }) => {
      if (focus && focus.grid === grid && focus.editing === key) byEmail.set(member.email, member);
    });
    records.forEach(({ member, record }) => {
      if (record && record.grid === grid && String(record.id) === key) byEmail.set(member.email, member);
    });
    return Array.from(byEmail.values());
  }, [peers, records, grid, rowId]);
};
